import {
  EUserRole,
  EPaymentInterval,
  EPaymentMethod,
  EInvoiceDeliveryMethod,
  ECompanyStatus,
  EPharmacyStatus,
  EPaymentStatus,
  EAuditStatus,
  EMedicineStatus,
  EInvoiceStatus,
} from './constants';
import {UploadFile} from 'antd';

export type TErrorData = {
  error_code: number;
  message: string;
};

export type TError = {
  status: number;
  data: TErrorData;
};

type DeepPartial<T> = {
  [P in keyof T]?: DeepPartial<T[P]>;
};

type TSortBy = 'created_at.asc' | 'created_at.desc';
type TFilterParams =
  | 'date:gte'
  | 'date:lte'
  | 'created_at:gte'
  | 'created_at:lte'
  | 'search_keyword:like';

export type TFilterArgs = {
  [key in TFilterParams]?: string;
};

type TPaginationMeta = {
  items_per_page: number;
  total_items: number;
  current_page: number;
  total_pages: number;
};

export type TPaginationArgs = {
  limit?: number;
  page?: number;
};

export type TSortByArgs = {
  sort_by?: TSortBy;
};

type TUserBase = {
  full_name: string;
  full_name_kana?: string;
  department?: string;
  position?: string;
};

export type TUser = TUserBase & {
  id: number;
  created_at: string;
  email?: string;
  role: EUserRole;
  payment?: IPayment;
  company: TCompanyUser;
  pharmacy?: TPharmacyUser;
};

export type TOwnEmailUpdate = {
  email: string;
  password: string;
};

export type TOwnEmailConfirm = {
  confirmation_code: string;
};

export type TOwnPasswordUpdate = {
  password: string;
  new_password: string;
};

export type TManagerBase = TUserBase & {
  email: string;
};

export type TManagerCreate = TManagerBase;

export type TManagerUpdate = Partial<TManagerBase>;

export type TManager = TManagerBase & {
  id: number;
  created_at: string;
};

export type TAddress = {
  zip_code: string;
  location: string;
  street: string;
};

export type TCompanyBase = TAddress & {
  name: string;
  phone_number?: string;
  coupon_code?: string;
};

export type TCompany = TCompanyBase & {
  id: number;
  created_at: string;
  status: ECompanyStatus;
  manager: TManager;
};

export type TCompanyUpdateOwn = Partial<TCompanyBase>;

export type TCompanyUser = Omit<TCompany, 'manager'>;

export type TPharmacyBase = TAddress & {
  name: string;
  facility_code?: string;
  phone_number: string;
};

export type TPharmacy = TPharmacyBase & {
  id: number;
  created_at: string;
  is_resecon_registered: boolean;
  status: EPharmacyStatus;
  manager: TManager;
};

export type TPharmacyUser = Omit<TPharmacy, 'manager'>;

export type TRegisterRequest = TManagerBase & {
  company: TCompanyBase;
  pharmacy: TPharmacyBase;
};

export type TRegisterResponse = TManager & {
  company: TCompany;
  pharmacy: TPharmacyUser;
};

export type TPharmacyCreate = TPharmacyBase & {
  manager: TManagerBase;
};

export type TPharmacyUpdate = DeepPartial<TPharmacyCreate> & {
  id: number;
};

export type TPharmacyUpdateOwn = Partial<TPharmacyBase>;

export type TPharmacyList = {
  meta: TPaginationMeta;
  data: TPharmacy[];
};

export type TInvitePharmacy = {
  email: string;
};

export type TCompletePharmacy = TUserBase & {
  pharmacy: TPharmacyBase;
  cognito_username: string;
  temporary_password: string;
  password: string;
};

export type TPayment = {
  id: number;
  method: EPaymentMethod;
  interval: EPaymentInterval;
  status: EPaymentStatus;
  created_at: string;
  amount: number;
  start_date: string;
  end_date: string;
  transaction_id: string;
  mf_billing_id: string;
  payment_id: number;
};

export type TPaymentList = {
  meta: TPaginationMeta;
  data: TPayment[];
};

export type TAuth = {
  access_token: string;
  id_token: string;
  refresh_token?: string;
  expires_in: number;
  token_type: string;
};

export type TUserState = {
  user?: TUser;
  auth?: TAuth;
  isFetching: boolean;
  isSuccess: boolean;
  isError: boolean;
  message: string;
};

export interface IPayment {
  id: string;
  method: EPaymentMethod;
  interval: EPaymentInterval;
}

export type TLogin = {
  phone_number?: string;
  username: string;
  password: string;
};

export type TLogout = {
  refresh_token?: string;
};

export type TResetPassword = {
  email: string;
  confirmation_code: string;
  password: string;
};

export type TPassword = {
  password: string;
};

export type TSetPassword = {
  cognito_username: string;
  temporary_password: string;
  password: string;
};

export type TForgotPassword = {
  email: string;
};

export interface TUnsubscribe {
  reason: string;
}

type TInvoiceBase = {
  interval: EPaymentInterval;
  delivery_methods: EInvoiceDeliveryMethod[];
  company_name: string;
  billing_name: string;
  billing_name_kana: string;
  department?: string;
  position?: string;
  zip_code: string;
  location: string;
  street: string;
  email: string;
  phone_number: string;
  representative_name: string;
  website: string;
};

export type TInvoice = TInvoiceBase & {
  id: string;
  created_at: string;
  status: EInvoiceStatus;
};

export type TInvoiceCreate = TInvoiceBase;

export type TInvoiceUpdate = Partial<TInvoiceCreate>;

export type TCardCreate = {
  interval: string;
  token: string;
};

export type TCardUpdate = {
  token: string;
};

export type TCard = {
  id: string;
  brand: string;
  exp_month: number;
  exp_year: number;
  last4: string;
  name: string;
  email?: string;
  phone?: string;
};

export type TFileCreate = {
  path: 'inquiry';
  name?: string;
  mime_type?: string;
  size?: number;
};

export type TFileUpdate = {
  id: number;
  parent_id: number;
};

export type TFile = {
  id: number;
  url: string;
};

type TInquiryBase = {
  text: string;
};

export type TInquiryCreate = TInquiryBase;

export type TInquiryForm = TInquiryBase & {
  title: string;
  attachments?: UploadFile[];
};

export type TInquiry = TInquiryBase & {
  id: number;
  created_at: string;
};

export type IRefreshTokenResponse = {
  access_token: string;
  expires_in: number;
  token_type: 'Bearer';
  id_token: string;
};

type TAuditBase = {
  reception_number: number;
  patient_name: string;
  auditor_name: string;
  clinic_name: string;
  department_name: string;
  doctor_name: string;
  status: EAuditStatus;
  has_one_package: boolean;
};

type TEvidence = {
  id: number;
  image_url: string;
};

type TPackage = {
  code: string;
  box_code: string;
};

export type TMedicine = {
  id: string;
  actual_name: string;
  original_name?: string;
  rp_number?: string;
  rp_directions?: string;
  rp_type?: string;
  rp_intake_unit?: string;
  rp_intake_frequency?: number;
  rp_intake_per_day?: number;
  intake_amount?: number;
  dosage: number;
  unit: string;
  status: EMedicineStatus;
  evidences: TEvidence[];
  is_one_package: boolean;
  packages: TPackage[];
};

export type TMedicineRp = Pick<
  TMedicine,
  | 'rp_number'
  | 'rp_directions'
  | 'rp_type'
  | 'rp_intake_unit'
  | 'rp_intake_frequency'
  | 'rp_intake_per_day'
> & {
  medicines: TMedicine[];
};

export type TInventory = {
  id: string;
  created_at: string;
  date: string;
  memo?: string;
  medicine_count: number;
};
export type TInventoryUpsertResponse = Omit<TInventory, 'medicine_count'>;
export type TInventoryList = {
  meta: TPaginationMeta;
  data: TInventory[];
};
export type TInventoryDetail = {
  id: string;
  created_at: string;
  box_count: number | undefined;
  piece_count_per_box: number | undefined;
  package_count: number | undefined;
  piece_count: number | undefined;
  expiration_date: string | undefined;
  standard_capacity: string | undefined;
  date: string;
  memo?: string;
  medicines: TInventoryMedicineList;
};
export type TInventoryMedicineUpdate = {
  box_count: number;
  piece_count_per_box: number;
  package_count: number;
  piece_count: number;
  expiration_date: string;
};
export type TInventoryMedicineList = {
  meta: TPaginationMeta;
  data: TInventoryMedicine[];
};
export type TInventoryMedicine = {
  id: string;
  created_at: string;
  yj_code: string;
  name: string;
  unit: string;
  standard_capacity: string;
  box_count: number;
  piece_count_per_box: number;
  package_unit: string;
  package_count: number;
  piece_count_per_package: number;
  piece_count: number;
  expiration_date: string;
  shelf_number: string;
};
export type TInventoryMedicineCreate = Omit<
  TInventoryMedicine,
  'id' | 'created_at'
>;

export type TAudit = TAuditBase & {
  id: number;
  created_at: string;
};

export type TAuditDetail = TAudit & {
  medicines: TMedicine[];
  evidences: TEvidence[];
};

export type TAuditList = {
  meta: TPaginationMeta;
  data: TAudit[];
};

export type TReseconBase = {
  name: string;
  ip_address: string;
  username: string;
  share_name: string;
  share_folder: string;
  password: string;
};

export type TResecon = TReseconBase & {
  id: number;
  created_at: string;
};

export type TReseconCreate = TReseconBase;

export type TReseconUpdate = Partial<TReseconCreate> & {
  id: number;
};

type TStaffBase = TUserBase & {
  username: string;
};

export type TStaffCreate = TStaffBase & {
  password: string;
};

export type TStaffUpdate = Partial<TStaffBase> & {
  id: number;
};

export type TStaff = TStaffBase & {
  id: number;
  created_at: string;
};

export type TStaffList = {
  meta: TPaginationMeta;
  data: TStaff[];
};

export type TManualUrls = {
  app_manual_url: string;
  web_manual_url: string;
};

export type TAppSource = {
  web: 'WEB';
  mobile: 'MOBILE';
};

type TAppLogBase = {
  event_name: string;
  message?: TLogMessageBody;
};

export type TAppLogCreate = TAppLogBase;

export type TAppLog = TAppLogBase & {
  id: number;
  created_at: string;
  app_os?: string;
  app_version?: string;
  app_source: TAppSource;
  device_model?: string;
  device_id?: string;
};

export type TLogMessageBody = {
  [key: string]: string | number | undefined | null | string[] | number[];
};

export const AuditType = {
  quantity: 'WEIGHT_SCALE',
  barcode: 'BARCODE',
};
